import { useEnvContext } from "context/EnvContext";
import { ASSESSMENT_SLUG, SEARCH_TYPE_CARE } from "core/consts";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  AccessDrainages as AccessDrainagesType,
  Auction,
  BaseCare as BaseCareType,
  Breathing as BreathingType,
  Companionship as CompanionshipType,
  CompressionCare as CompressionType,
  Dialysis as DialysisType,
  Food as FoodType,
  HelpAtHome as HelpAtHomeType,
  IncontinenceHelp as IncontinenceType,
  Injection as InjectionType,
  Medication as MedicationType,
  Palliative as PalliativeType,
  PatientNeedMonitoring,
  PatientNeedSupport,
  PsychiatricCare as PsychiatricCareType,
  IntegrationAssistance as IntegrationAssistanceType,
  WoundCare as WoundType,
} from "core/types";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { PatientInfoSlugContext } from ".";
import {
  BooleanField,
  Categories,
  CategoryDescription,
  EmptiableCategory,
  EmptiableField,
  PatientProfileInfoCard,
  StringField,
} from "./shared";
import {
  ComponentMappingItem,
  descriptionCategory,
  exists,
  Filter,
  getCategories,
  stringFilled,
} from "./utils";

function Nutrition({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: FoodType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="nutrition"
      title={translations.patient.careNeeds.nutrition}
    >
      <EmptiableField
        testId="parenteral_nutrition"
        title={translations.patient.careNeeds.parenteralNutrition.label}
        value={value?.parenteral_nutrition}
      />
      <EmptiableField
        testId="enteral_nutrition"
        title={translations.patient.careNeeds.enteralNutrition.label}
        value={value?.enteral_nutrition}
      />
    </EmptiableCategory>
  );
}

function Incontinence({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: IncontinenceType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="incontinence"
      title={translations.patient.careNeeds.incontinence}
    >
      <EmptiableField
        testId="urinary_incontinence"
        title={translations.patient.careNeeds.urinaryIncontinence}
        value={value?.urinary_incontinence}
      />
      <EmptiableField
        testId="fecal_incontinence"
        title={translations.patient.careNeeds.fecalIncontinence}
        value={value?.fecal_incontinence}
      />
    </EmptiableCategory>
  );
}

function Breathing({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: BreathingType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="breathing"
      title={translations.patient.careNeeds.breathing}
    >
      <EmptiableField
        testId="tracheostomy"
        title={translations.patient.careNeeds.tracheostomy.label}
        value={value?.tracheostomy}
      />
      <BooleanField
        hasValue={value?.specialised_intensive_nurse}
        label={translations.ontologies.service.values.specialisedIntensiveNurse}
      />
      <EmptiableField
        testId="oxygen_therapy"
        title={translations.patient.careNeeds.oxygenTherapy.label}
        value={value?.oxygen_therapy}
      />
    </EmptiableCategory>
  );
}

function Dialysis({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: DialysisType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="dialysis"
      title={translations.patient.careNeeds.dialysis}
    >
      <EmptiableField
        testId="hemodialysis"
        title={translations.patient.careNeeds.hemodialysis.label}
        value={value?.hemodialysis}
      />
      <EmptiableField
        testId="peritoneal_dialysis"
        title={translations.patient.careNeeds.peritonealDialysis.label}
        value={value?.peritoneal_dialysis}
      />
    </EmptiableCategory>
  );
}

function Wounds({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: WoundType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="wound_care"
      title={translations.patient.careNeeds.woundCare}
    >
      <StringField testId="woundCare_description" value={value?.description} />
    </EmptiableCategory>
  );
}

function PsychiatricCare({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: PsychiatricCareType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="psychiatric_care"
      title={translations.patient.careNeeds.psychiatric_care.label}
    >
      <StringField
        testId="psychiatric_care_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

function IntegrationAssistance({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: IntegrationAssistanceType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="integration_assistance"
      title={translations.patient.careNeeds.integrationAssistance.label}
    >
      <StringField testId="integration_assistance" value={value?.description} />
    </EmptiableCategory>
  );
}

function Medication({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: MedicationType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="medication"
      title={translations.patient.careNeeds.medication}
    >
      <EmptiableField
        testId="medication_description"
        value={value?.description}
      />
      <EmptiableField
        testId="blood_thinner"
        title={translations.patient.takesBloodThinner}
        value={value?.blood_thinner}
      />
    </EmptiableCategory>
  );
}

function Injection({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: InjectionType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="injection"
      title={translations.patient.careNeeds.injection}
    >
      <StringField testId="injection_description" value={value?.description} />
    </EmptiableCategory>
  );
}

function AccessDrainages({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: AccessDrainagesType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="access_and_drainages"
      title={translations.patient.careNeeds.accessDrainages.accessAndDrainages}
    >
      <EmptiableField
        testId="tracheal_cannula"
        title={translations.patient.careNeeds.accessDrainages.trachealCannula}
        value={value?.tracheal_cannula}
      />
      <EmptiableField
        testId="gastric_tube"
        title={translations.patient.careNeeds.accessDrainages.gastricTube}
        value={value?.gastric_tube}
      />
      <EmptiableField
        testId="central_venous_catheter"
        title={
          translations.patient.careNeeds.accessDrainages.centralVenousCatheter
        }
        value={value?.central_venous_catheter}
      />
      <EmptiableField
        testId="peripheral_venous_catheter"
        title={
          translations.patient.careNeeds.accessDrainages
            .peripheralVenousCatheter
        }
        value={value?.peripheral_venous_catheter}
      />
      <EmptiableField
        testId="suprapubic_catheter"
        title={
          translations.patient.careNeeds.accessDrainages.suprapubicCatheter
        }
        value={value?.suprapubic_catheter}
      />
      <EmptiableField
        testId="permanent_catheter"
        title={translations.patient.careNeeds.accessDrainages.permanentCatheter}
        value={value?.permanent_catheter}
      />
      <EmptiableField
        testId="drainage"
        title={translations.patient.careNeeds.accessDrainages.drainage}
        value={value?.drainage}
      />
      <EmptiableField
        testId="intravenous_injection"
        title={translations.patient.intravenousInjection}
        value={value?.intravenous_injection}
      />
      <EmptiableField
        testId="other_access_drainages"
        title={translations.patient.careNeeds.accessDrainages.other}
        value={value?.other_access_drainages}
      />
    </EmptiableCategory>
  );
}

function Remarks({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: string | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="remarks"
      title={translations.patient.careNeeds.remarks}
    >
      <StringField testId="remarks" value={value} />
    </EmptiableCategory>
  );
}

function Compression({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: CompressionType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="compression"
      title={translations.patient.careNeeds.compression}
    >
      <StringField
        testId="compression_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}
function PalliativeCare({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: PalliativeType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="palliative_care"
      title={translations.patient.palliativeCare.label}
    >
      <CategoryDescription value={value?.description} />
      <BooleanField
        hasValue={value?.palliative_certificate_needed}
        label={
          translations.patient.medicalDiagnosis.palliativeCertificateNeeded
        }
      />
    </EmptiableCategory>
  );
}

function BaseCare({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: BaseCareType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="base_care"
      title={translations.patient.careNeeds.baseCare}
    >
      <CategoryDescription value={value?.description} />
      <StringField
        testId="companionship_grant"
        value={
          value?.companionship_grant
            ? translations.patient.careNeeds.carePaidByHealthInsurance
            : ""
        }
      />
    </EmptiableCategory>
  );
}

function BasicMedicalCare({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: { description: string | null | undefined } | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="medical_care"
      title={translations.patient.careNeeds.medicalCare}
    >
      <StringField
        testId="medical_care_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

function StaticCareForDisabledPatients({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: { description: string | null | undefined } | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="static_care_disabled_patients"
      title={translations.ontologies.service.values.staticCareDisabledPatients}
    >
      <StringField
        testId="disabled_patients_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

function ImportantInformation({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: string | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="Important_information"
      title={translations.patient.careNeeds.titleImportantInformationCare}
    >
      <StringField testId="important_info" value={value} />
    </EmptiableCategory>
  );
}

function Companionship({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: CompanionshipType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="companionship"
      title={translations.patient.careNeeds.companionship}
    >
      <CategoryDescription value={value?.description} />
      <StringField
        testId="companionship_grant"
        value={
          value?.companionship_grant
            ? translations.patient.careNeeds.companionshipGrant
            : ""
        }
      />
    </EmptiableCategory>
  );
}

function HelpAtHome({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: HelpAtHomeType | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="help_at_home"
      title={translations.patient.careNeeds.helpAtHome}
    >
      <StringField
        testId="help_at_home_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

function Support({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: PatientNeedSupport | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="need_support"
      title={translations.patient.careNeeds.needSupport}
    >
      <BooleanField
        hasValue={value?.eating}
        label={translations.patient.careNeeds.withEating}
      />
      <BooleanField
        hasValue={value?.body_care}
        label={translations.patient.careNeeds.withBodyCare}
      />
      <BooleanField
        hasValue={value?.dressing}
        label={translations.patient.careNeeds.withDressing}
      />
    </EmptiableCategory>
  );
}

function Monitoring({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: PatientNeedMonitoring | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="monitoring"
      title={translations.patient.careNeeds.monitoring}
    >
      <EmptiableField
        prefix={translations.patient.careNeeds.circulation.label}
        testId="circulation"
        value={value?.circulation}
      />
      <EmptiableField
        prefix={translations.patient.careNeeds.respiration.label}
        testId="respiration"
        value={value?.respiration}
      />
      <EmptiableField
        prefix={translations.patient.careNeeds.metabolism.label}
        testId="metabolism"
        value={value?.metabolism}
      />
    </EmptiableCategory>
  );
}

function CareForChildrenAndTeenagers({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: { description: string | null | undefined } | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="care_for_children_and_teenagers"
      title={translations.patient.careNeeds.careForChildrenAndTeenagers.title}
    >
      <StringField
        testId="care_for_children_and_teenagers_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

function CareForYouth({
  isEmpty,
  translations,
  value,
}: {
  isEmpty: boolean;
  translations: Translations;
  value: { description: string | null | undefined } | null | undefined;
}) {
  return (
    <EmptiableCategory
      isEmpty={isEmpty}
      testId="care_for_youth"
      title={translations.patient.careNeeds.careForYouth.title}
    >
      <StringField
        testId="care_for_youth_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

export const careneedsFilters: Record<string, Filter> = {
  access_drainages: {
    exists: (auction) => !!auction?.patient?.care_needs?.access_drainages,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.access_drainages &&
      (auction?.patient?.care_needs.access_drainages.tracheal_cannula != null ||
        auction?.patient?.care_needs.access_drainages.gastric_tube != null ||
        auction?.patient?.care_needs.access_drainages.central_venous_catheter !=
          null ||
        auction?.patient?.care_needs.access_drainages
          .peripheral_venous_catheter != null ||
        auction?.patient?.care_needs.access_drainages.suprapubic_catheter !=
          null ||
        auction?.patient?.care_needs.access_drainages.permanent_catheter !=
          null ||
        auction?.patient?.care_needs.access_drainages.drainage != null ||
        auction?.patient?.care_needs.access_drainages.intravenous_injection !=
          null ||
        auction?.patient?.care_needs.access_drainages.other_access_drainages !=
          null),
    valueGetter: (auction) => auction?.patient?.care_needs?.access_drainages,
  },
  base_care: {
    exists: (auction) => !!auction?.patient?.care_needs?.base_care,
    notEmpty: (auction) =>
      (auction?.patient?.care_needs?.base_care &&
        (stringFilled(auction?.patient?.care_needs.base_care.description) ||
          auction?.patient?.care_needs.base_care?.companionship_grant)) ===
      true,
    valueGetter: (auction) => auction?.patient?.care_needs?.base_care,
  },
  basic_medical_care: {
    exists: (auction) => !!auction?.patient?.care_needs?.basic_medical_care,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.basic_medical_care?.description,
    valueGetter: (auction) => auction?.patient?.care_needs?.basic_medical_care,
  },
  breathing: {
    exists: (auction) => !!auction?.patient?.care_needs?.breathing,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.breathing &&
      (stringFilled(auction?.patient?.care_needs.breathing.tracheostomy) ||
        auction?.patient?.care_needs.breathing?.specialised_intensive_nurse ||
        (auction?.patient?.care_needs.breathing &&
          stringFilled(
            auction?.patient?.care_needs.breathing.oxygen_therapy,
          ))) === true,
    valueGetter: (auction) => auction?.patient?.care_needs?.breathing,
  },
  care_for_children_and_teenagers: {
    exists: (auction) =>
      !!auction?.patient?.care_needs?.care_for_children_and_teenagers,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.care_for_children_and_teenagers
        ?.description,
    valueGetter: (auction) =>
      auction?.patient?.care_needs?.care_for_children_and_teenagers,
  },
  care_for_youth: {
    exists: (auction) => !!auction?.patient?.care_needs?.care_for_youth,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.care_for_youth?.description,
    valueGetter: (auction) => auction?.patient?.care_needs?.care_for_youth,
  },
  companionship: {
    exists: (auction) => !!auction?.patient?.care_needs?.companionship,
    notEmpty: (auction) =>
      (auction?.patient?.care_needs?.companionship &&
        (stringFilled(auction?.patient?.care_needs.companionship.description) ||
          auction?.patient?.care_needs.companionship?.companionship_grant)) ===
      true,
    valueGetter: (auction) => auction?.patient?.care_needs?.companionship,
  },
  compression: {
    exists: (auction) => !!auction?.patient?.care_needs?.compression,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.compression),
    valueGetter: (auction) => auction?.patient?.care_needs?.compression,
  },
  Dialysis: {
    exists: (auction) => !!auction?.patient?.care_needs?.dialysis,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.dialysis &&
      (stringFilled(
        auction?.patient?.care_needs.dialysis.peritoneal_dialysis,
      ) || stringFilled(auction?.patient?.care_needs.dialysis.hemodialysis)) ===
        true,
    valueGetter: (auction) => auction?.patient?.care_needs?.dialysis,
  },
  help_at_home: {
    exists: (auction) => !!auction?.patient?.care_needs?.help_at_home,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.help_at_home),
    valueGetter: (auction) => auction?.patient?.care_needs?.help_at_home,
  },

  important_information: {
    exists: (auction) => !!auction?.patient?.care_needs?.important_information,
    notEmpty: (auction) =>
      stringFilled(auction?.patient?.care_needs?.important_information) ===
      true,
    valueGetter: (auction) =>
      auction?.patient?.care_needs?.important_information,
  },
  incontinence: {
    exists: (auction) => !!auction?.patient?.care_needs?.incontinence,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.incontinence &&
      (stringFilled(
        auction?.patient?.care_needs.incontinence.urinary_incontinence,
      ) ||
        (auction?.patient?.care_needs.incontinence &&
          stringFilled(
            auction?.patient?.care_needs.incontinence.fecal_incontinence,
          ))) === true,
    valueGetter: (auction) => auction?.patient?.care_needs?.incontinence,
  },
  injection: {
    exists: (auction) => !!auction?.patient?.care_needs?.injection,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.injection),
    valueGetter: (auction) => auction?.patient?.care_needs?.injection,
  },
  integration_assistance: {
    exists: (auction) => !!auction?.patient?.care_needs?.integration_assistance,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.integration_assistance),
    valueGetter: (auction) =>
      auction?.patient?.care_needs?.integration_assistance,
  },
  medication: {
    exists: (auction) => !!auction?.patient?.care_needs?.medication,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      (auction?.patient?.care_needs.medication?.description != null ||
        auction?.patient?.care_needs?.medication?.blood_thinner != null),
    valueGetter: (auction) => auction?.patient?.care_needs?.medication,
  },
  monitoring: {
    exists: (auction) => auction?.patient?.care_needs?.monitoring != null,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      ((auction?.patient?.care_needs.monitoring != null &&
        stringFilled(auction?.patient?.care_needs.monitoring.circulation)) ||
        (auction?.patient?.care_needs.monitoring != null &&
          stringFilled(auction?.patient?.care_needs.monitoring.respiration)) ||
        (auction?.patient?.care_needs.monitoring != null &&
          stringFilled(auction?.patient?.care_needs.monitoring.metabolism))),
    valueGetter: (auction) => auction?.patient?.care_needs?.monitoring,
  },
  nutrition: {
    exists: (auction) => !!auction?.patient?.care_needs?.nutrition,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.nutrition &&
      ((auction?.patient?.care_needs.nutrition &&
        stringFilled(
          auction?.patient?.care_needs.nutrition.parenteral_nutrition,
        )) ||
        (auction?.patient?.care_needs.nutrition &&
          stringFilled(
            auction?.patient?.care_needs.nutrition.enteral_nutrition,
          ))) === true,
    valueGetter: (auction) => auction?.patient?.care_needs?.nutrition,
  },
  palliative_care: {
    exists: (auction) => auction?.patient?.care_needs?.palliative != null,
    notEmpty: (auction) =>
      auction?.patient?.care_needs?.palliative != null &&
      (auction?.patient?.care_needs.palliative.description != null ||
        auction?.patient?.care_needs.palliative.palliative_certificate_needed !=
          null),
    valueGetter: (auction) => auction?.patient?.care_needs?.palliative,
  },
  psychiatric_care: {
    exists: (auction) => !!auction?.patient?.care_needs?.psychiatric_care,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.psychiatric_care),
    valueGetter: (auction) => auction?.patient?.care_needs?.psychiatric_care,
  },
  remarks: {
    exists: (auction) =>
      auction?.patient?.care_needs?.remarks != null &&
      auction?.patient?.care_needs.remarks != "",
    notEmpty: (auction) =>
      auction?.patient?.care_needs?.remarks != null &&
      auction?.patient?.care_needs.remarks != "",
    valueGetter: (auction) => auction?.patient?.care_needs?.remarks,
  },
  static_care_disabled_patients: {
    exists: (auction) =>
      !!auction?.patient?.care_needs?.static_care_disabled_patients,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.static_care_disabled_patients
        ?.description,
    valueGetter: (auction) =>
      auction?.patient?.care_needs?.static_care_disabled_patients,
  },
  support: {
    exists: (auction) => auction?.patient?.care_needs?.support != null,
    notEmpty: (auction) =>
      auction?.patient?.care_needs?.support != null &&
      !!(
        auction?.patient?.care_needs.support.eating ||
        auction?.patient?.care_needs.support.body_care ||
        auction?.patient?.care_needs.support.dressing
      ),
    valueGetter: (auction) => auction?.patient?.care_needs?.support,
  },
  Wounds: {
    exists: (auction) => !!auction?.patient?.care_needs?.wound_care,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.wound_care),
    valueGetter: (auction) => auction?.patient?.care_needs?.wound_care,
  },
};

const careneedsComponentMapping: ComponentMappingItem[] = [
  {
    Component: Support,
    key: "support",
  },
  {
    Component: PalliativeCare,
    key: "palliative_care",
  },
  {
    Component: PsychiatricCare,
    key: "psychiatric_care",
  },
  {
    Component: IntegrationAssistance,
    key: "integration_assistance",
  },
  {
    Component: Nutrition,
    key: "nutrition",
  },
  {
    Component: Incontinence,
    key: "incontinence",
  },
  {
    Component: Breathing,
    key: "breathing",
  },
  {
    Component: Dialysis,
    key: "Dialysis",
  },
  {
    Component: Wounds,
    key: "Wounds",
  },
  {
    Component: Medication,
    key: "medication",
  },
  {
    Component: Monitoring,
    key: "monitoring",
  },
  {
    Component: Compression,
    key: "compression",
  },
  {
    Component: Injection,
    key: "injection",
  },
  {
    Component: AccessDrainages,
    key: "access_drainages",
  },
  {
    Component: ImportantInformation,
    key: "important_information",
  },

  {
    Component: BaseCare,
    key: "base_care",
  },
  {
    Component: BasicMedicalCare,
    key: "basic_medical_care",
  },
  {
    Component: StaticCareForDisabledPatients,
    key: "static_care_disabled_patients",
  },
  {
    Component: Companionship,
    key: "companionship",
  },
  {
    Component: HelpAtHome,
    key: "help_at_home",
  },
  {
    Component: CareForChildrenAndTeenagers,
    key: "care_for_children_and_teenagers",
  },
  {
    Component: CareForYouth,
    key: "care_for_youth",
  },
  {
    Component: Remarks,
    key: "remarks",
  },
];

export function CareNeedsCard({ auction }: { auction: Auction }) {
  const getOntology = useGetOntology();
  const { isSenderApp } = useEnvContext();
  const translations = useTranslations();

  if (!auction.patient.care_needs) return null;

  const categories = getCategories({
    auction,
    componentMapping: careneedsComponentMapping,
    filters: careneedsFilters,
    forClinic: isSenderApp,
  });

  if (!exists(categories)) return null;

  return (
    <PatientInfoSlugContext.Provider
      value={{
        assessmentSlug: ASSESSMENT_SLUG.NEEDS,
        auctionId: auction.id,
        patientId: auction.patient.id,
      }}
    >
      <PatientProfileInfoCard
        title={
          auction.search_type === SEARCH_TYPE_CARE
            ? translations.patient.careNeeds.stepperTitleCare
            : translations.patient.careNeeds.stepperTitle
        }
      >
        <Categories
          auction={auction}
          categories={categories}
          getOntology={getOntology}
          translations={translations}
        />
      </PatientProfileInfoCard>
    </PatientInfoSlugContext.Provider>
  );
}
